import React, { useMemo, useState } from 'react';
import KPICard, { KPIGrid } from '../KPICard';
import DataTable from '../DataTable';
import WellAcreageMap from '../charts/WellAcreageMap';
import { formatInt, formatCurrency } from '../../utils/format';
import { openDrillSelection, selectionFromWellMark } from '../../utils/drilldown';
import { useDeal } from '../../deals/DealContext';
import { apiKey } from '../../utils/wellIdentity';

const STATUS_FILTERS = ['ALL', 'PDP', 'PDSI', 'P&A'];

function pvMm(pv10M) {
  if (pv10M == null) return null;
  return pv10M > 100 ? pv10M / 1000 : pv10M;
}

export default function MapDashboard({ data }) {
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedWellKeys, setSelectedWellKeys] = useState([]);
  const { deal } = useDeal();
  const isOil = deal?.primaryProduct === 'oil';
  const { kpis, wells, notes, boswell, concentration } = data;
  const focusWell = concentration || boswell;
  const drillWell = (d) => openDrillSelection(selectionFromWellMark(d), { path: 'map' });
  const selectedWellKeySet = useMemo(() => new Set(selectedWellKeys), [selectedWellKeys]);

  const tableRows = useMemo(() => {
    const list =
      statusFilter === 'ALL' ? wells : wells.filter((w) => w.status === statusFilter);
    return [...list]
      .sort((a, b) => (pvMm(b.pv10M) || 0) - (pvMm(a.pv10M) || 0))
      .map((w) => ({
        ...w,
        pv10Display: pvMm(w.pv10M),
        coords: `${w.lat.toFixed(4)}, ${w.lng.toFixed(4)}`,
      }));
  }, [wells, statusFilter]);

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">Wells & Acreage Map</h2>
          <p className="page-subtitle">
            {isOil
              ? `${(deal?.counties || []).join(' & ') || 'West TX'} · well locations · acreage layers when published`
              : 'Robertson & Limestone · RRC surface holes · VDR Bald Prairie shapefiles'}
          </p>
          <div className="page-quote">
            <span className="page-quote-label">{data.quoteLabel}</span>
            <span>&ldquo;{data.quote}&rdquo;</span>
          </div>
        </div>
      </div>

      <KPIGrid>
        <KPICard
          label="Wells plotted"
          value={formatInt(kpis.wellsPlotted)}
          sub={`${kpis.pdp} PDP · ${kpis.pdsi} PDSI · ${kpis.pa} P&A`}
          color="var(--navy)"
        />
        <KPICard
          label="Drilling units"
          value={formatInt(kpis.drillingUnits)}
          sub={`${formatInt(kpis.leaseTracts)} lease tracts`}
          color="var(--amber)"
        />
        <KPICard
          label="Net acres"
          value={formatInt(Math.round(kpis.netAcres))}
          sub={`Gross ${formatInt(Math.round(kpis.grossAcres))}`}
          color="var(--teal)"
        />
        <KPICard
          label={isOil ? 'Top concentration' : 'Boswell #1'}
          value={focusWell?.pv10M != null ? formatCurrency(pvMm(focusWell.pv10M) * 1e6) : '—'}
          sub={
            focusWell?.lat != null && focusWell?.lng != null
              ? `${focusWell.lat.toFixed(3)}°, ${focusWell.lng.toFixed(3)}°`
              : focusWell?.lease || '—'
          }
          color="var(--green)"
        />
      </KPIGrid>

      <section className="dashboard-section">
        <div className="chart-card map-card">
          <div className="map-toolbar">
            <div>
              <div className="chart-title">Interactive map</div>
              <div className="chart-subtitle">
                Toggle layers · marker size ≈ PV10 · click / Shift-click to select · open detail
                from popup, toolbar, or table
              </div>
            </div>
            <div className="map-filters">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`map-filter-btn ${statusFilter === s ? 'active' : ''}`}
                  onClick={() => setStatusFilter(s)}
                >
                  {s === 'ALL' ? 'All wells' : s}
                </button>
              ))}
            </div>
          </div>

          <WellAcreageMap
            data={data}
            statusFilter={statusFilter}
            onWellSelect={drillWell}
            selectedWellKeys={selectedWellKeys}
            onSelectionChange={setSelectedWellKeys}
          />

          <div className="map-legend">
            <span className="map-legend-item">
              <i style={{ background: '#10B981' }} /> PDP
            </span>
            <span className="map-legend-item">
              <i style={{ background: '#E8A317' }} /> PDSI
            </span>
            <span className="map-legend-item">
              <i style={{ background: '#94A3B8' }} /> P&A
            </span>
            <span className="map-legend-item">
              <i className="map-legend-line" style={{ background: '#E8A317' }} /> Drilling unit
            </span>
            <span className="map-legend-item">
              <i className="map-legend-poly" style={{ background: '#1B355C' }} /> Lease tract
            </span>
            <span className="map-legend-note">
              Larger markers = higher PV10
              {isOil ? '' : ' · Boswell outlined'}
              {!data.layers?.drillingUnits && !data.layers?.leaseStatus
                ? ' · acreage layers not published for this deal'
                : ''}
            </span>
          </div>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="chart-card">
          <div className="chart-title">Well locations</div>
          <div className="chart-subtitle">
            {tableRows.length} wells
            {statusFilter !== 'ALL' ? ` · filtered to ${statusFilter}` : ''}
          </div>
          <DataTable
            columns={[
              { key: 'lease', label: 'Well / lease' },
              { key: 'status', label: 'Status' },
              { key: 'wellType', label: 'Type' },
              { key: 'county', label: 'County' },
              {
                key: 'pv10Display',
                label: 'PV10 $MM',
                format: (v) => (v == null ? '—' : v.toFixed(2)),
              },
              { key: 'api', label: 'API' },
              { key: 'coords', label: 'Lat / Lng' },
            ]}
            data={tableRows}
            defaultSort="pv10Display"
            onRowClick={drillWell}
            rowClassName={(row) => {
              const k = apiKey(row.api) || row.api;
              return k && selectedWellKeySet.has(k) ? 'dtable-row-selected' : '';
            }}
          />
        </div>
      </section>

      {notes?.length > 0 && (
        <section className="dashboard-section">
          <div className="chart-card">
            <div className="chart-title">Source notes</div>
            <ul className="bullet-list">
              {notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}
